<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const DEFAULT_TEMPLATE_FORM = {
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  header: '',
  body: '',
  footer: '',
  headerExamples: [],
  bodyExamples: [],
};

export default {
  components: {
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editingTemplate: null,
      confirmingDeleteTemplateKey: null,
      isDeletingTemplateKey: null,
      isSavingTemplate: false,
      isSyncingTemplates: false,
      isTemplateFormOpen: false,
      templateForm: { ...DEFAULT_TEMPLATE_FORM },
    };
  },
  computed: {
    categoryOptions() {
      return ['UTILITY', 'MARKETING'];
    },
    bodyVariableKeys() {
      return this.extractVariableKeys(this.templateForm.body);
    },
    headerVariableKeys() {
      return this.extractVariableKeys(this.templateForm.header);
    },
    templateFormTitle() {
      return this.isEditingTemplate
        ? this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.FORM_TITLE_EDIT')
        : this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.FORM_TITLE_CREATE');
    },
    templateSubmitLabel() {
      return this.isEditingTemplate
        ? this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SAVE_BUTTON')
        : this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CREATE_BUTTON');
    },
    templateSuccessMessage() {
      return this.isEditingTemplate
        ? this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.UPDATE_SUCCESS')
        : this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CREATE_SUCCESS');
    },
    isEditingTemplate() {
      return !!this.editingTemplate;
    },
    isTemplateFormInvalid() {
      return (
        !this.templateForm.name.trim() ||
        !this.templateForm.language.trim() ||
        !this.templateForm.category.trim() ||
        !this.templateForm.body.trim() ||
        this.hasMissingExamples(
          this.bodyVariableKeys,
          this.templateForm.bodyExamples
        ) ||
        this.hasMissingExamples(
          this.headerVariableKeys,
          this.templateForm.headerExamples
        )
      );
    },
    metaTemplates() {
      return Array.isArray(this.inbox.message_templates)
        ? this.inbox.message_templates
        : [];
    },
    metaTemplateStats() {
      return this.metaTemplates.reduce(
        (stats, template) => {
          const status = this.normalizedTemplateStatus(template);
          stats.total += 1;
          stats[status] = (stats[status] || 0) + 1;
          if (this.isTemplateSupported(template)) stats.supported += 1;

          return stats;
        },
        { total: 0, approved: 0, pending: 0, rejected: 0, supported: 0 }
      );
    },
    hasMetaTemplates() {
      return this.metaTemplates.length > 0;
    },
  },
  watch: {
    'templateForm.body': 'syncTemplateExamples',
    'templateForm.header': 'syncTemplateExamples',
  },
  methods: {
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        await this.$store.dispatch('inboxes/get');
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async submitTemplate() {
      this.syncTemplateExamples();
      if (this.isTemplateFormInvalid) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.REQUIRED_FIELDS')
        );
        return;
      }

      this.isSavingTemplate = true;
      try {
        await this.$store.dispatch(this.templateActionName(), {
          inboxId: this.inbox.id,
          templateId: this.editingTemplate?.id,
          template: this.templatePayload(),
        });
        useAlert(this.templateSuccessMessage);
        this.closeTemplateForm();
      } catch (error) {
        useAlert(this.errorMessage(error));
      } finally {
        this.isSavingTemplate = false;
      }
    },
    requestDeleteTemplate(template) {
      this.confirmingDeleteTemplateKey = this.templateKey(template);
    },
    cancelDeleteTemplate() {
      this.confirmingDeleteTemplateKey = null;
    },
    async deleteTemplate(template) {
      this.isDeletingTemplateKey = this.templateKey(template);
      try {
        await this.$store.dispatch('inboxes/deleteMetaTemplate', {
          inboxId: this.inbox.id,
          template: {
            id: template.id,
            name: template.name,
            language: template.language,
          },
        });
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.DELETE_SUCCESS')
        );
      } catch (error) {
        useAlert(this.errorMessage(error));
      } finally {
        this.isDeletingTemplateKey = null;
        this.confirmingDeleteTemplateKey = null;
      }
    },
    openNewTemplateForm() {
      this.editingTemplate = null;
      this.templateForm = { ...DEFAULT_TEMPLATE_FORM };
      this.isTemplateFormOpen = true;
    },
    openEditTemplateForm(template) {
      const header = template.components?.find(
        component => component.type === 'HEADER' && component.format === 'TEXT'
      );
      const body = template.components?.find(
        component => component.type === 'BODY'
      );
      const footer = template.components?.find(
        component => component.type === 'FOOTER'
      );

      this.editingTemplate = template;
      this.templateForm = {
        name: template.name || '',
        category: template.category || 'UTILITY',
        language: template.language || 'pt_BR',
        header: header?.text || '',
        body: body?.text || '',
        footer: footer?.text || '',
        headerExamples: header?.example?.header_text || [],
        bodyExamples: body?.example?.body_text?.[0] || [],
      };
      this.isTemplateFormOpen = true;
      this.syncTemplateExamples();
    },
    closeTemplateForm() {
      this.editingTemplate = null;
      this.templateForm = { ...DEFAULT_TEMPLATE_FORM };
      this.isTemplateFormOpen = false;
    },
    templateActionName() {
      return this.isEditingTemplate
        ? 'inboxes/updateMetaTemplate'
        : 'inboxes/createMetaTemplate';
    },
    templatePayload() {
      return {
        name: this.templateForm.name.trim(),
        category: this.templateForm.category,
        language: this.templateForm.language.trim(),
        header: this.templateForm.header.trim(),
        body: this.templateForm.body.trim(),
        footer: this.templateForm.footer.trim(),
        header_examples: this.templateForm.headerExamples,
        body_examples: this.templateForm.bodyExamples,
      };
    },
    normalizeTemplateName() {
      if (this.isEditingTemplate) return;

      this.templateForm.name = this.templateForm.name
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    syncTemplateExamples() {
      this.templateForm.bodyExamples = this.syncedExamples(
        this.bodyVariableKeys,
        this.templateForm.bodyExamples
      );
      this.templateForm.headerExamples = this.syncedExamples(
        this.headerVariableKeys,
        this.templateForm.headerExamples
      );
    },
    syncedExamples(keys, examples) {
      return keys.map((_, index) => examples[index] || '');
    },
    hasMissingExamples(keys, examples) {
      return keys.some((_, index) => !examples[index]?.trim());
    },
    extractVariableKeys(text = '') {
      const matches = text.match(/{{\s*[a-zA-Z0-9_]+\s*}}/g) || [];
      return [
        ...new Set(matches.map(match => match.replace(/[{}]/g, '').trim())),
      ];
    },
    normalizedTemplateStatus(template) {
      return template?.status?.toLowerCase() || 'unknown';
    },
    normalizedTemplateCategory(template) {
      return template?.category || 'UNKNOWN';
    },
    templateBodyPreview(template) {
      const body = template.components?.find(
        component => component.type === 'BODY'
      );
      return (
        body?.text ||
        this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NO_BODY')
      );
    },
    templateComponentVariables(template) {
      return (template.components || []).reduce((count, component) => {
        const textMatches = component.text?.match(/{{[^}]+}}/g) || [];
        const buttonMatches = (component.buttons || []).flatMap(
          button => button.url?.match(/{{[^}]+}}/g) || []
        );
        return count + textMatches.length + buttonMatches.length;
      }, 0);
    },
    isTemplateSupported(template) {
      if (!template?.components) return false;
      if (this.normalizedTemplateStatus(template) !== 'approved') return false;
      if (template.category === 'AUTHENTICATION') return false;

      return !template.components.some(
        component =>
          ['LIST', 'PRODUCT', 'CATALOG', 'CALL_PERMISSION_REQUEST'].includes(
            component.type
          ) ||
          (component.type === 'HEADER' && component.format === 'LOCATION')
      );
    },
    isTextTemplateEditable(template) {
      return (
        !!template.id &&
        Array.isArray(template.components) &&
        template.category !== 'AUTHENTICATION' &&
        !template.components?.some(
          component =>
            component.type === 'BUTTONS' ||
            (component.type === 'HEADER' && component.format !== 'TEXT') ||
            ['LIST', 'PRODUCT', 'CATALOG', 'CALL_PERMISSION_REQUEST'].includes(
              component.type
            )
        )
      );
    },
    templateSupportLabel(template) {
      return this.isTemplateSupported(template)
        ? this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SUPPORTED')
        : this.$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NOT_SUPPORTED');
    },
    templateStatusLabel(template) {
      const status = this.normalizedTemplateStatus(template).toUpperCase();
      switch (status) {
        case 'APPROVED':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.APPROVED'
          );
        case 'PENDING':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.PENDING'
          );
        case 'REJECTED':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.REJECTED'
          );
        case 'PAUSED':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.PAUSED'
          );
        case 'DISABLED':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.DISABLED'
          );
        case 'IN_APPEAL':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.IN_APPEAL'
          );
        default:
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUSES.UNKNOWN'
          );
      }
    },
    templateCategoryLabel(category) {
      const normalizedCategory = category || 'UNKNOWN';
      switch (normalizedCategory) {
        case 'UTILITY':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORIES.UTILITY'
          );
        case 'MARKETING':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORIES.MARKETING'
          );
        case 'AUTHENTICATION':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORIES.AUTHENTICATION'
          );
        default:
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORIES.UNKNOWN'
          );
      }
    },
    templateStatusClass(template) {
      const status = this.normalizedTemplateStatus(template);
      if (status === 'approved') return 'bg-n-teal-3 text-n-teal-11';
      if (status === 'rejected') return 'bg-n-ruby-3 text-n-ruby-11';
      if (status === 'pending') return 'bg-n-amber-3 text-n-amber-11';

      return 'bg-n-slate-3 text-n-slate-11';
    },
    templateSupportClass(template) {
      return this.isTemplateSupported(template)
        ? 'bg-n-blue-3 text-n-blue-11'
        : 'bg-n-slate-3 text-n-slate-11';
    },
    templateKey(template) {
      return template.id || `${template.name}-${template.language}`;
    },
    errorMessage(error) {
      return (
        error?.response?.data?.error ||
        this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
      );
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-7xl">
    <div class="flex flex-col gap-4">
      <div
        class="flex flex-col gap-4 p-4 rounded-lg outline outline-1 outline-n-weak bg-n-surface-1"
      >
        <div
          class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
        >
          <div>
            <h3 class="text-heading-2 text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SUBTITLE') }}
            </p>
          </div>

          <div class="flex flex-wrap gap-2">
            <NextButton
              faded
              slate
              icon="i-lucide-plus"
              :disabled="isSavingTemplate"
              @click="openNewTemplateForm"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NEW_BUTTON') }}
            </NextButton>
            <NextButton
              icon="i-lucide-refresh-ccw"
              :is-loading="isSyncingTemplates"
              :disabled="isSyncingTemplates"
              @click="syncTemplates"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SYNC_BUTTON') }}
            </NextButton>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div class="p-3 rounded-lg bg-n-alpha-black2">
            <p class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.TOTAL') }}
            </p>
            <p class="text-heading-2 text-n-slate-12">
              {{ metaTemplateStats.total }}
            </p>
          </div>
          <div class="p-3 rounded-lg bg-n-alpha-black2">
            <p class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.APPROVED') }}
            </p>
            <p class="text-heading-2 text-n-slate-12">
              {{ metaTemplateStats.approved || 0 }}
            </p>
          </div>
          <div class="p-3 rounded-lg bg-n-alpha-black2">
            <p class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SUPPORTED') }}
            </p>
            <p class="text-heading-2 text-n-slate-12">
              {{ metaTemplateStats.supported || 0 }}
            </p>
          </div>
          <div class="p-3 rounded-lg bg-n-alpha-black2">
            <p class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.PENDING') }}
            </p>
            <p class="text-heading-2 text-n-slate-12">
              {{ metaTemplateStats.pending || 0 }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="isTemplateFormOpen"
        class="flex flex-col gap-4 p-4 rounded-lg outline outline-1 outline-n-weak bg-n-surface-1"
      >
        <div class="flex items-center justify-between gap-3">
          <h4 class="text-heading-3 text-n-slate-12">
            {{ templateFormTitle }}
          </h4>
          <NextButton faded slate icon="i-lucide-x" @click="closeTemplateForm">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CANCEL_BUTTON') }}
          </NextButton>
        </div>

        <div class="grid gap-3 md:grid-cols-3">
          <label class="flex flex-col gap-1">
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NAME') }}
            </span>
            <input
              v-model="templateForm.name"
              :disabled="isEditingTemplate"
              class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NAME_PLACEHOLDER')
              "
              @blur="normalizeTemplateName"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORY') }}
            </span>
            <select
              v-model="templateForm.category"
              class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
            >
              <option
                v-for="category in categoryOptions"
                :key="category"
                :value="category"
              >
                {{ templateCategoryLabel(category) }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-label-small text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LANGUAGE') }}
            </span>
            <input
              v-model="templateForm.language"
              class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LANGUAGE_PLACEHOLDER'
                )
              "
            />
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.HEADER') }}
          </span>
          <input
            v-model="templateForm.header"
            class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.HEADER_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BODY') }}
          </span>
          <textarea
            v-model="templateForm.body"
            rows="5"
            class="px-3 py-2 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12 resize-y"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BODY_PLACEHOLDER')
            "
          />
        </label>

        <div
          v-if="headerVariableKeys.length || bodyVariableKeys.length"
          class="grid gap-3 md:grid-cols-2"
        >
          <label
            v-for="(variable, index) in headerVariableKeys"
            :key="`header-${variable}`"
            class="flex flex-col gap-1"
          >
            <span class="text-label-small text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.HEADER_VARIABLE_EXAMPLE',
                  { variable }
                )
              }}
            </span>
            <input
              v-model="templateForm.headerExamples[index]"
              class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.VARIABLE_EXAMPLE_PLACEHOLDER'
                )
              "
            />
          </label>

          <label
            v-for="(variable, index) in bodyVariableKeys"
            :key="`body-${variable}`"
            class="flex flex-col gap-1"
          >
            <span class="text-label-small text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BODY_VARIABLE_EXAMPLE',
                  { variable }
                )
              }}
            </span>
            <input
              v-model="templateForm.bodyExamples[index]"
              class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.VARIABLE_EXAMPLE_PLACEHOLDER'
                )
              "
            />
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.FOOTER') }}
          </span>
          <input
            v-model="templateForm.footer"
            class="h-10 px-3 rounded-lg border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.FOOTER_PLACEHOLDER')
            "
          />
        </label>

        <div class="flex justify-end">
          <NextButton
            icon="i-lucide-save"
            :is-loading="isSavingTemplate"
            :disabled="isSavingTemplate"
            @click="submitTemplate"
          >
            {{ templateSubmitLabel }}
          </NextButton>
        </div>
      </div>

      <div
        v-if="hasMetaTemplates"
        class="overflow-x-auto rounded-lg outline outline-1 outline-n-weak bg-n-surface-1"
      >
        <div class="min-w-[60rem]">
          <div
            class="grid grid-cols-[minmax(14rem,1.3fr)_7rem_8rem_7rem_6rem_9rem] gap-3 px-3 py-2 bg-n-alpha-black2 text-label-small text-n-slate-11"
          >
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.TEMPLATE') }}
            </span>
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LANGUAGE') }}
            </span>
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORY') }}
            </span>
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUS') }}
            </span>
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.VARIABLES') }}
            </span>
            <span>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.ACTIONS') }}
            </span>
          </div>
          <div class="overflow-y-auto max-h-[32rem]">
            <div
              v-for="template in metaTemplates"
              :key="templateKey(template)"
              class="grid grid-cols-[minmax(14rem,1.3fr)_7rem_8rem_7rem_6rem_9rem] gap-3 px-3 py-3 border-t border-n-weak"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p
                    class="min-w-0 truncate text-sm font-medium text-n-slate-12"
                  >
                    {{ template.name }}
                  </p>
                  <span
                    class="px-2 py-0.5 text-xs rounded-md"
                    :class="templateSupportClass(template)"
                  >
                    {{ templateSupportLabel(template) }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-n-slate-11 line-clamp-2">
                  {{ templateBodyPreview(template) }}
                </p>
              </div>
              <p class="text-sm text-n-slate-12">
                {{ template.language || '-' }}
              </p>
              <p class="text-sm text-n-slate-12">
                {{
                  templateCategoryLabel(normalizedTemplateCategory(template))
                }}
              </p>
              <span
                class="self-start px-2 py-0.5 text-xs rounded-md"
                :class="templateStatusClass(template)"
              >
                {{ templateStatusLabel(template) }}
              </span>
              <p class="text-sm text-n-slate-12">
                {{ templateComponentVariables(template) }}
              </p>
              <div class="flex items-start gap-1">
                <template
                  v-if="confirmingDeleteTemplateKey === templateKey(template)"
                >
                  <NextButton
                    faded
                    ruby
                    xs
                    icon="i-lucide-check"
                    :is-loading="
                      isDeletingTemplateKey === templateKey(template)
                    "
                    :disabled="isDeletingTemplateKey === templateKey(template)"
                    :title="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CONFIRM_BUTTON'
                      )
                    "
                    @click="deleteTemplate(template)"
                  />
                  <NextButton
                    faded
                    slate
                    xs
                    icon="i-lucide-x"
                    :disabled="isDeletingTemplateKey === templateKey(template)"
                    :title="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CANCEL_BUTTON'
                      )
                    "
                    @click="cancelDeleteTemplate"
                  />
                </template>
                <template v-else>
                  <NextButton
                    faded
                    slate
                    xs
                    icon="i-lucide-pencil"
                    :disabled="!isTextTemplateEditable(template)"
                    :title="
                      $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EDIT_BUTTON')
                    "
                    @click="openEditTemplateForm(template)"
                  />
                  <NextButton
                    faded
                    ruby
                    xs
                    icon="i-lucide-trash-2"
                    :disabled="!!isDeletingTemplateKey"
                    :title="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.DELETE_BUTTON'
                      )
                    "
                    @click="requestDeleteTemplate(template)"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="px-3 py-8 text-center rounded-lg outline outline-1 outline-n-weak bg-n-surface-1"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EMPTY') }}
        </p>
      </div>
    </div>
  </div>
</template>
